// src/redux/store/index.ts

import { createStore, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import reducers from '../reducers/index';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import channelThemeReducer from '../reducers/channelThemeReducer';

// ---- your slice-state types ----
import { IAuthState } from '../../types/auth';
import { IDeviceState } from '../../types/device';
import { IDrawerState } from '../../types/drawer';
import { ITeamState } from '../../types/team';
import { IPolicyState } from '../../types/policy';
import { IChildState } from '../../types/child';
import { INavigationHistory } from '../../types/generics';
import { INotificationState } from '../../types/notification';
import { IPolicyRequestState } from '../../types/policyRequest';
import { IAppChatState, IChatState } from '../../types/chat';
import { IProgramState } from '../../types/program';
import { IProgramFormState } from '../../types/programForm';
import { IDashboardState } from '../../types/dashboard';
import { IProgramEnrollmentState } from '../../types/programEnrollment';
import { IChannelThemeState } from '../../types/channelTheme';
import { ICommunityRolesState } from '../../types/communityRoles';
import { INotificationSettingsState } from '../reducers/notificationSettingsReducer';
import { ITestManagementState } from '../reducers/testManagementReducer';
import { IContentLogsState } from '../reducers/contentLogsReducer';
import { IAnalyticsState } from '../reducers/analyticsReducer';

export interface RootState {
  drawerReducer: IDrawerState;
  auth: IAuthState;
  teamReducer: ITeamState;
  devicesReducer: IDeviceState;
  childReducer: IChildState;
  policyReducer: IPolicyState;
  defaultDownTimeReducer: any;
  navigationHistoryReducer: INavigationHistory;
  notificationReducer: INotificationState;
  policyRequests: IPolicyRequestState;
  chatReducer: IChatState;
  appChatReducer: IAppChatState;
  programReducer: IProgramState;
  programFormReducer: IProgramFormState;
  dashboardReducer: IDashboardState;
  myResultReducer: any;
  programEnrollmentReducer: IProgramEnrollmentState;
  channelThemeReducer: IChannelThemeState;
  communityRolesReducer: ICommunityRolesState;
  notificationSettingsReducer: INotificationSettingsState;
  testManagementReducer: ITestManagementState;
  contentLogsReducer: IContentLogsState;
  analyticsReducer: IAnalyticsState;
}

const persistConfig = {
  key: 'root',
  storage,
  // never persist these slices:
  // - programFormReducer / programReducer: hold React Context / circular refs
  // - drawerReducer: must reset to default (isOpen: true) on every reload so
  //   mobile users always see the sidebar / channel list on fresh load. Without
  //   this, a stale `isOpen: false` from a previous channel visit persists and
  //   hides the sidebar permanently on mobile.
  blacklist: ['programFormReducer', 'programReducer', 'drawerReducer'],
};

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: typeof compose;
  }
}
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const persistedReducer = persistReducer(persistConfig, reducers);

export default () => {
  const store = createStore(
    persistedReducer,
    composeEnhancers(applyMiddleware(thunk))
  );
  const persistor = persistStore(store);
  return { store, persistor };
};
